import React from 'react';
import { Settings, TrendingUp, BarChart2 } from 'lucide-react';

const AdditionalOptionsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Settings className="w-5 h-5 mr-2 text-indigo-500" />
        Дополнительные параметры расчета
      </h3>
      
      {/* Ускоренная амортизация */}
      <div className="mb-3">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeAccelerationCoefficient"
            checked={formData.includeAccelerationCoefficient}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Использовать коэффициент ускоренной амортизации
          </span>
        </label>
        
        {formData.includeAccelerationCoefficient && (
          <div className="mt-2 ml-6">
            <label htmlFor="accelerationCoefficient" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Коэффициент ускорения
            </label>
            <select
              id="accelerationCoefficient"
              name="accelerationCoefficient"
              value={formData.accelerationCoefficient}
              onChange={handleInputChange}
              className={`form-select w-full sm:w-1/3 ${errors.accelerationCoefficient ? 'input-error' : ''}`}
            >
              <option value="1.5">1.5</option>
              <option value="2">2</option>
              <option value="2.5">2.5</option>
              <option value="3">3</option>
            </select>
            {errors.accelerationCoefficient && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.accelerationCoefficient}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Применяется для высокотехнологичного оборудования, работающего в агрессивной среде
            </p>
          </div>
        )}
      </div>
      
      {/* Переоценка */}
      <div className="mb-3">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeRevaluation"
            checked={formData.includeRevaluation}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Учитывать переоценку основного средства
          </span>
        </label>
        
        {formData.includeRevaluation && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2 ml-6">
            <div>
              <label htmlFor="revaluationAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Сумма переоценки (₽)
              </label>
              <input
                type="text"
                id="revaluationAmount"
                name="revaluationAmount"
                value={formData.revaluationAmount}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.revaluationAmount ? 'input-error' : ''}`}
                placeholder="Сумма"
              />
              {errors.revaluationAmount && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.revaluationAmount}
                </p>
              )}
            </div>
            
            <div>
              <label htmlFor="revaluationYear" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Год переоценки
              </label>
              <input
                type="text"
                id="revaluationYear"
                name="revaluationYear"
                value={formData.revaluationYear}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.revaluationYear ? 'input-error' : ''}`}
                placeholder="Год"
              />
              {errors.revaluationYear && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.revaluationYear}
                </p>
              )}
            </div>
            
            <div>
              <label htmlFor="revaluationType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Тип переоценки
              </label>
              <select
                id="revaluationType"
                name="revaluationType"
                value={formData.revaluationType}
                onChange={handleInputChange}
                className="form-select w-full"
              >
                <option value="increase">Дооценка (увеличение)</option>
                <option value="decrease">Уценка (уменьшение)</option>
              </select>
            </div>
          </div>
        )}
      </div>
      
      {/* Разбивка по месяцам */}
      <div>
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeMonthlySplit"
            checked={formData.includeMonthlySplit}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Рассчитывать помесячную амортизацию
          </span>
        </label>
        <p className="mt-1 ml-6 text-xs text-gray-500 dark:text-gray-400">
          Расчет амортизации с разбивкой по месяцам, как требуется в налоговом учете
        </p>
      </div>

      {/* Информационный блок */}
      <div className="mt-4 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-100 dark:border-green-800">
        <div className="flex items-start">
          <TrendingUp className="w-4 h-4 text-green-500 mt-1 mr-2" />
          <div>
            <h4 className="text-sm font-medium text-green-700 dark:text-green-300">
              О переоценке основных средств
            </h4>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              В соответствии с ПБУ 6/01 и ФСБУ 6/2020 предприятия вправе переоценивать основные средства не чаще одного раза в год. 
              При переоценке пропорционально изменяется как первоначальная стоимость, так и накопленная амортизация 
              на дату проведения переоценки.
            </p>
          </div>
        </div>
      </div>
      
      {formData.includeAccelerationCoefficient && (
        <div className="mt-3 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg border border-orange-100 dark:border-orange-800">
          <div className="flex items-start">
            <BarChart2 className="w-4 h-4 text-orange-500 mt-1 mr-2" />
            <div>
              <h4 className="text-sm font-medium text-orange-700 dark:text-orange-300">
                Важно о коэффициентах ускоренной амортизации
              </h4>
              <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                В России коэффициент ускоренной амортизации можно использовать в следующих случаях:
                <br />- Для основных средств, работающих в агрессивной среде или условиях повышенной сменности (коэффициент до 2)
                <br />- Для предприятий с лизинговым имуществом (коэффициент до 3)
                <br />- Для резидентов особых экономических зон (коэффициент до 2)
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdditionalOptionsSection;